/**
 * Zentrale Alerts (Marcel 2026-10-06): ALLE Hinweise im Web – Erfolg, Fehler, Warnung, Info – erscheinen als
 * zentrierter Alert in der Bildschirmmitte (wie AppAlert in der App), nie mehr als Inline-Text irgendwo auf der Seite.
 * Aufruf überall (auch außerhalb von React): appAlert.success('Gespeichert.'), appAlert.error('Speichern fehlgeschlagen').
 */
import { apiErrorMessage } from '../../services/api';

export type AppAlertType = 'success' | 'error' | 'warning' | 'info';
export interface AppAlertOptions {
  type: AppAlertType;
  message: string;
  title?: string;
  /** ms bis zum automatischen Schließen; Standard: Erfolg 2500, sonst 0 (bleibt bis OK) */
  autoCloseMs?: number;
}
export interface AppAlertItem extends AppAlertOptions { id: number }

type Listener = (queue: AppAlertItem[]) => void;
let queue: AppAlertItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l([...queue]));

/** PURE: Standard-Auto-Close je Typ */
export const defaultAutoClose = (type: AppAlertType): number => (type === 'success' ? 2500 : 0);

/** PURE: Fehlerobjekt/Unbekanntes in eine Meldung verwandeln – EINE Stelle: apiErrorMessage aus services/api (filtert Technik-Texte wie „Request failed…“) */
export const toMessage = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return apiErrorMessage(value, fallback);
  return fallback;
};

export const appAlert = {
  show(opts: AppAlertOptions): number {
    const message = String(opts.message ?? '').trim();
    if (!message) return 0;
    const id = nextId++;
    queue = [...queue, { ...opts, message, autoCloseMs: opts.autoCloseMs ?? defaultAutoClose(opts.type), id }];
    emit();
    return id;
  },
  success(message: string, title?: string) { return appAlert.show({ type: 'success', message, title }); },
  error(message: unknown, title?: string, fallback = 'Es ist ein Fehler aufgetreten.') { return appAlert.show({ type: 'error', message: toMessage(message, fallback), title }); },
  warning(message: string, title?: string) { return appAlert.show({ type: 'warning', message, title }); },
  info(message: string, title?: string) { return appAlert.show({ type: 'info', message, title }); },
  dismiss(id: number) { queue = queue.filter((a) => a.id !== id); emit(); },
  clear() { queue = []; emit(); },
  subscribe(listener: Listener): () => void { listeners.add(listener); listener([...queue]); return () => { listeners.delete(listener); }; },
  /** nur für Tests */
  _peek(): AppAlertItem[] { return [...queue]; },
};

export default appAlert;
